/**
 * COReviewTab - Read-only view of Compliance Officer review feedback
 *
 * Shows decision summary and comments grouped by section, ordered by severity.
 * Sections with 0 comments are hidden. Clicking a section header navigates
 * to that step in the KYC form.
 */

import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  XCircle,
  Info,
  Clock,
  User,
  ChevronRight,
} from 'lucide-react';
import { KYC_STEPS } from '../KYCWizardContext';
import { formatDateTime } from '../../../utils/formatters';
import type { KYCReview, KYCReviewComment, CommentSeverity } from '../../../types/complianceWorkflow';

interface COReviewTabProps {
  review: KYCReview;
  comments: KYCReviewComment[];
  onNavigateToStep?: (step: number) => void;
}

/**
 * Severity styling for CO review comments (CommentSeverity type).
 * Intentionally distinct from the SEVERITY_CONFIG in AlertsAndActions — these map
 * to review comment severities (info/warning/action_required), NOT system-level
 * event severities (info/warning/error/critical).
 */
const SEVERITY_CONFIG: Record<CommentSeverity, { icon: React.ElementType; color: string; bg: string; label: string; order: number }> = {
  action_required: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Action Required', order: 0 },
  warning: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', label: 'Warning', order: 1 },
  info: { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Info', order: 2 },
};

const COReviewTab: React.FC<COReviewTabProps> = ({ review, comments, onNavigateToStep }) => {

  // Group comments by section, only include sections with comments
  const commentsBySection = KYC_STEPS
    .map((step) => ({
      step,
      comments: comments
        .filter((c) => c.section_id === step.section)
        .sort((a, b) => {
          const orderA = SEVERITY_CONFIG[a.severity]?.order ?? 3;
          const orderB = SEVERITY_CONFIG[b.severity]?.order ?? 3;
          return orderA - orderB;
        }),
    }))
    .filter((group) => group.comments.length > 0);

  const unresolvedCount = comments.filter((c) => !c.is_resolved).length;

  return (
    <div className="flex flex-col h-full">
      {/* Decision summary */}
      {(() => {
        const isRejected = review.decision === 'reject';
        const summaryBg = isRejected ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
        const SummaryIcon = isRejected ? XCircle : AlertTriangle;
        const iconColor = isRejected ? 'text-red-600' : 'text-amber-600';
        const titleColor = isRejected ? 'text-red-900' : 'text-amber-900';
        const textColor = isRejected ? 'text-red-800' : 'text-amber-800';
        const metaColor = isRejected ? 'text-red-700' : 'text-amber-700';
        const badgeBg = isRejected ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800';
        const title = isRejected ? 'KYC Profile Rejected' : 'Changes Requested';

        return (
          <div className={`${summaryBg} border-b p-4 shrink-0`}>
            <div className="flex items-center gap-2 mb-2">
              <SummaryIcon className={`w-5 h-5 ${iconColor}`} />
              <h3 className={`text-sm font-semibold ${titleColor}`}>{title}</h3>
            </div>

            {review.rejection_category && isRejected && (
              <p className={`text-xs ${metaColor} mb-1`}>
                Category: {review.rejection_category.replace(/_/g, ' ')}
              </p>
            )}

            {review.decision_notes && (
              <p className={`text-sm ${textColor} mb-3`}>{review.decision_notes}</p>
            )}

            <div className={`flex flex-wrap items-center gap-3 text-xs ${metaColor}`}>
              {review.reviewer_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {review.reviewer_name}
                </span>
              )}
              {review.decided_at && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDateTime(review.decided_at)}
                </span>
              )}
              {unresolvedCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full ${badgeBg} font-medium`}>
                  {unresolvedCount} unresolved
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Comments by section */}
      <div className="flex-1 overflow-y-auto">
        {commentsBySection.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
            No comments from the reviewer.
          </div>
        ) : (
          <div className="p-3 space-y-4">
            {commentsBySection.map(({ step, comments: sectionComments }) => {
              const sectionUnresolved = sectionComments.filter((c) => !c.is_resolved).length;

              return (
                <div key={step.id}>
                  {/* Section header — clickable to navigate */}
                  <button
                    onClick={() => onNavigateToStep?.(step.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{step.name}</span>
                      {sectionUnresolved > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          {sectionUnresolved}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </button>

                  {/* Comment cards */}
                  <div className="mt-2 space-y-2 pl-1">
                    {sectionComments.map((comment) => {
                      const config = SEVERITY_CONFIG[comment.severity] || SEVERITY_CONFIG.info;
                      const Icon = config.icon;

                      return (
                        <div
                          key={comment.id}
                          className={`rounded-lg border p-3 ${
                            comment.is_resolved ? 'bg-gray-50 border-gray-200 opacity-60' : config.bg
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <Icon
                              className={`w-4 h-4 mt-0.5 shrink-0 ${
                                comment.is_resolved ? 'text-gray-400' : config.color
                              }`}
                            />
                            <div className="flex-1 min-w-0">
                              {/* Severity badge */}
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider mb-1 ${
                                  comment.is_resolved ? 'bg-gray-200 text-gray-500' : `${config.bg}`
                                }`}
                              >
                                {config.label}
                              </span>

                              <p
                                className={`text-sm ${
                                  comment.is_resolved ? 'text-gray-500 line-through' : 'text-gray-900'
                                }`}
                              >
                                {comment.comment}
                              </p>

                              {comment.field_path && (
                                <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 font-mono">
                                  {comment.field_path}
                                </span>
                              )}

                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                                <span>{comment.created_by_name || 'Reviewer'}</span>
                                <span>{formatDateTime(comment.created_at)}</span>
                                {comment.is_resolved && (
                                  <span className="text-green-600">Resolved</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default COReviewTab;
